import React, { useState, useEffect } from 'react';
import { database, auth } from '../firebase';
import { ref, push, onValue, set, remove, serverTimestamp, off } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';

const TaxForm = () => {
  const [taxes, setTaxes] = useState([]);
  const [currentPage, setCurrentPage] = useState('table'); // 'table', 'addForm', 'editForm'
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [editingTax, setEditingTax] = useState(null);

  // Encode email for Firebase path
  const encodeEmailForPath = (email) => {
    return email.replace(/[.#$[\]]/g, '_');
  };

  // Styles matching CategoriesManager
  const styles = {
    container: {
      maxWidth: '100%',
      margin: '0 auto',
      padding: '2rem',
      minHeight: '100vh',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '2rem',
      padding: '1.5rem',
      backgroundColor: 'white',
      borderRadius: '6px',
      border: '1px solid #d1d5db',
      fontWeight: '600',
      color: '#374151',
    },
    title: {
      fontSize: '2rem',
      fontWeight: '700',
      color: '#2563eb',
      margin: '0',
    },
    addButton: {
      padding: '0.875rem 2rem',
      borderRadius: '8px',
      backgroundColor: '#2563eb',
      color: 'white',
      fontWeight: '600',
      cursor: 'pointer',
      border: 'none',
      fontSize: '0.95rem',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 15px rgba(37, 99, 235, 0.3)',
      transform: 'translateY(0)',
    },
    tableContainer: {
      backgroundColor: 'white',
      borderRadius: '16px',
      overflow: 'hidden',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e2e8f0',
      overflowX: 'auto',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: '0.9rem',
      border: '1px solid #d1d5db',
    },
    tableHeader: {
      padding: '1rem 0.75rem',
      textAlign: 'center',
      backgroundColor: '#2563eb',
      color: 'white',
      fontWeight: '600',
      fontSize: '0.85rem',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      border: '1px solid #1e40af',
    },
    tableCell: {
      padding: '1rem 0.75rem',
      border: '1px solid #d1d5db',
      color: '#374151',
      verticalAlign: 'middle',
      textAlign: 'center',
    },
    noData: {
      textAlign: 'center',
      padding: '4rem 2rem',
      color: '#718096',
      fontSize: '1.1rem',
    },
    srNoCell: {
      fontWeight: '600',
      textAlign: 'center',
      width: '60px',
    },
    actionCell: {
      display: 'flex',
      gap: '0.5rem',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '0.5rem',
    },
    editButton: {
      padding: '0.5rem',
      borderRadius: '6px',
      backgroundColor: '#10b981',
      color: 'white',
      fontWeight: '500',
      cursor: 'pointer',
      border: 'none',
      fontSize: '0.8rem',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
      transform: 'translateY(0)',
    },
    deleteButton: {
      padding: '0.5rem',
      borderRadius: '6px',
      backgroundColor: '#ef4444',
      color: 'white',
      fontWeight: '500',
      cursor: 'pointer',
      border: 'none',
      fontSize: '0.8rem',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 8px rgba(220, 38, 38, 0.3)',
      transform: 'translateY(0)',
    },
    formContainer: {
      padding: '2rem',
    },
    formLabel: {
      display: 'block',
      color: '#374151',
      fontWeight: 'bold',
      fontSize: '1rem',
      marginBottom: '0.5rem',
    },
    formInput: {
      width: '70%',
      padding: '0.5rem 0.75rem',
      fontSize: '0.9rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      outline: 'none',
      transition: 'border-color 0.2s, box-shadow 0.2s',
      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
      boxSizing: 'border-box',
    },
    formSelect: {
      width: '70%',
      padding: '0.5rem 0.75rem',
      fontSize: '0.9rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      outline: 'none',
      transition: 'border-color 0.2s, box-shadow 0.2s',
      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
      boxSizing: 'border-box',
      backgroundColor: 'white',
       backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
      backgroundPosition: 'right 0.5rem center',
      backgroundRepeat: 'no-repeat',
      backgroundSize: '0.8em 0.8em',
      appearance: 'none',
    },
    formHint: {
      color: '#6b7280',
      fontSize: '0.75rem',
      marginTop: '0.25rem',
      margin: '0.25rem 0 0 0',
    },
  };

  // Auth listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setCurrentUser(user);
      } else {
        setCurrentUser(null);
        setTaxes([]);
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  // Fetch taxes from Firebase
  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    const encodedEmail = encodeEmailForPath(currentUser.email);
    const taxesRef = ref(database, `users/${encodedEmail}/gstTaxes`);

    const handleData = (snapshot) => {
      const taxesData = [];
      snapshot.forEach((childSnapshot) => {
        taxesData.push({
          id: childSnapshot.key,
          ...childSnapshot.val(),
        });
      });
      setTaxes(taxesData);
      setLoading(false);
    };

    const handleError = (error) => {
      console.error('Error fetching taxes:', error);
      setLoading(false);
    };

    onValue(taxesRef, handleData, handleError);

    return () => {
      off(taxesRef, 'value', handleData);
    };
  }, [currentUser]);

  const AddEditTaxPage = ({ editTax = null }) => {
    const [taxData, setTaxData] = useState({
      description: editTax?.description || '',
      taxType: editTax?.taxType || 'SELECT TAX TYPE',
      cgst: editTax?.cgst || '',
      sgst: editTax?.sgst || '',
      igst: editTax?.igst || '',
    });

    const handleChange = (e) => {
      const { name, value } = e.target;
      setTaxData(prev => ({ ...prev, [name]: value }));
    };

    const handleSave = () => {
      if (!currentUser) {
        window.alert('You must be logged in to add or edit a tax');
        return;
      }

      if (taxData.description.trim() === '' || taxData.taxType === 'SELECT TAX TYPE') {
        window.alert('Please fill in Tax Description and select a Tax Type');
        return;
      }

      const encodedEmail = encodeEmailForPath(currentUser.email);
      const taxEntry = {
        description: taxData.description,
        taxType: taxData.taxType,
        cgst: taxData.cgst ? parseFloat(taxData.cgst) : 0,
        sgst: taxData.sgst ? parseFloat(taxData.sgst) : 0,
        igst: taxData.igst ? parseFloat(taxData.igst) : 0,
        createdBy: currentUser.email,
        createdAt: editTax ? editTax.createdAt : serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      if (editTax) {
        // Update existing tax
        const taxRef = ref(database, `users/${encodedEmail}/gstTaxes/${editTax.id}`);
        set(taxRef, taxEntry)
          .then(() => {
            window.alert('Tax updated successfully!');
            setCurrentPage('table');
            setEditingTax(null);
          })
          .catch((error) => {
            window.alert('Error updating tax: ' + error.message);
          });
      } else {
        // Add new tax
        const taxesRef = push(ref(database, `users/${encodedEmail}/gstTaxes`));
        set(taxesRef, taxEntry)
          .then(() => {
            window.alert('Tax added successfully!');
            setCurrentPage('table');
            setTaxData({ description: '', taxType: 'SELECT TAX TYPE', cgst: '', sgst: '', igst: '' });
          })
          .catch((error) => {
            window.alert('Error adding tax: ' + error.message);
          });
      }
    };

    const handleCancel = () => {
      setCurrentPage('table');
      setEditingTax(null);
    };

    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <button
              onClick={handleCancel}
              style={{
                padding: '0.5rem',
                borderRadius: '0.5rem',
                border: 'none',
                backgroundColor: 'transparent',
                cursor: 'pointer',
                transition: 'background-color 0.2s',
                display: 'flex',
                alignItems: 'center'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              title="Back to Taxes"
            >
              <svg style={{ width: '1.5rem', height: '1.5rem', color: '#6b7280' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 style={styles.title}>
              {editTax ? 'Edit Tax' : 'Add New Tax'}
            </h1>
          </div>
        </div>

        <div style={styles.tableContainer}>
          <div style={styles.formContainer}>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: 'bold',
              color: '#2563eb',
              marginBottom: '1rem'
            }}>
              Tax Information
            </h2>
            
            <div style={{ maxWidth: '32rem' }}>
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={styles.formLabel}>Tax Description *</label>
                <input
                  type="text"
                  name="description"
                  value={taxData.description}
                  onChange={handleChange}
                  style={styles.formInput}
                  onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                  onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                  placeholder="Enter tax description"
                />
                <p style={styles.formHint}>Provide a descriptive name for the tax</p>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={styles.formLabel}>Tax Type *</label>
                <select
                  name="taxType"
                  value={taxData.taxType}
                  onChange={handleChange}
                  style={styles.formSelect}
                  onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                  onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                >
                  <option value="SELECT TAX TYPE">Select Tax type</option>
                  <option value="EXEMPTED">Exempted</option>
                  <option value="INCLUSIVE">Inclusive</option>
                  <option value="VAT">Vat</option>
                </select>
                <p style={styles.formHint}>Select the appropriate tax type</p>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={styles.formLabel}>CGST (%)</label>
                <input
                  type="number"
                  name="cgst"
                  value={taxData.cgst}
                  onChange={handleChange}
                  style={styles.formInput}
                  onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                  onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                  placeholder="Enter CGST percentage"
                />
                <p style={styles.formHint}>Enter CGST percentage (e.g., 9)</p>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={styles.formLabel}>SGST (%)</label>
                <input
                  type="number"
                  name="sgst"
                  value={taxData.sgst}
                  onChange={handleChange}
                  style={styles.formInput}
                  onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                  onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                  placeholder="Enter SGST percentage"
                />
                <p style={styles.formHint}>Enter SGST percentage (e.g., 9)</p>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={styles.formLabel}>IGST (%)</label>
                <input
                  type="number"
                  name="igst"
                  value={taxData.igst}
                  onChange={handleChange}
                  style={styles.formInput}
                  onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                  onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                  placeholder="Enter IGST percentage"
                />
                <p style={styles.formHint}>Enter IGST percentage (e.g., 18)</p>
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <button
                  onClick={handleSave}
                  style={styles.editButton}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 20px rgba(16, 185, 129, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 15px rgba(16, 185, 129, 0.3)';
                  }}
                >
                  💾 {editTax ? 'Update Tax' : 'Save Tax'}
                </button>
                <button
                  onClick={handleCancel}
                  style={{
                    ...styles.editButton,
                    backgroundColor: '#6b7280',
                    boxShadow: '0 4px 15px rgba(107, 114, 128, 0.3)',
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 20px rgba(107, 114, 128, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 15px rgba(107, 114, 128, 0.3)';
                  }}
                >
                  ❌ Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleEdit = (tax) => {
    setEditingTax(tax);
    setCurrentPage('editForm');
  };

  const handleDelete = (taxId) => {
    if (window.confirm('Are you sure you want to delete this tax?')) {
      if (!currentUser) return;

      const encodedEmail = encodeEmailForPath(currentUser.email);
      const taxRef = ref(database, `users/${encodedEmail}/gstTaxes/${taxId}`);
      
      set(taxRef, null)
        .then(() => {
          // The onValue listener will update the taxes state
        })
        .catch((error) => {
          console.error("Error deleting tax: ", error);
        });
    }
  };

  if (currentPage === 'addForm') {
    return <AddEditTaxPage />;
  }

  if (currentPage === 'editForm' && editingTax) {
    return <AddEditTaxPage editTax={editingTax} />;
  }

  if (!currentUser) {
    return (
      <div style={styles.container}>
        <div style={styles.noData}>
          <div style={{ fontSize: '4rem', marginBottom: '1rem', opacity: '0.5' }}>🔐</div>
          <h3 style={{ color: '#4a5568', marginBottom: '0.5rem' }}>GST Taxes</h3>
          <p>Please log in to view and manage your taxes.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>GST Taxes</h1>
        <button
          onClick={() => setCurrentPage('addForm')}
          style={styles.addButton}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 6px 20px rgba(37, 99, 235, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 4px 15px rgba(37, 99, 235, 0.3)';
          }}
        >
          + Add New Tax
        </button>
      </div>

      <div style={styles.tableContainer}>
        {loading ? (
          <div style={styles.noData}>
            <p>⏳ Loading taxes...</p>
          </div>
        ) : taxes.length > 0 ? (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{ ...styles.tableHeader, ...styles.srNoCell }}>Sr.No</th>
                <th style={styles.tableHeader}>Description</th>
                <th style={styles.tableHeader}>Type</th>
                <th style={styles.tableHeader}>CGST (%)</th>
                <th style={styles.tableHeader}>SGST (%)</th>
                <th style={styles.tableHeader}>IGST (%)</th>
                <th style={styles.tableHeader}>Action</th>
              </tr>
            </thead>
            <tbody>
              {taxes.map((tax, index) => (
                <tr 
                  key={tax.id} 
                  style={{
                    backgroundColor: index % 2 === 0 ? '#f7fafc' : 'white',
                    transition: 'background-color 0.2s ease'
                  }}
                >
                  <td style={{ ...styles.tableCell, ...styles.srNoCell }}>{index + 1}</td>
                  <td style={styles.tableCell}>{tax.description}</td>
                  <td style={styles.tableCell}>{tax.taxType}</td>
                  <td style={styles.tableCell}>{tax.cgst || '0'}</td>
                  <td style={styles.tableCell}>{tax.sgst || '0'}</td>
                  <td style={styles.tableCell}>{tax.igst || '0'}</td>
                  <td style={{ ...styles.tableCell, ...styles.actionCell }}>
                    <button
                      onClick={() => handleEdit(tax)}
                      style={styles.editButton}
                      title="Edit Tax"
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-1px)';
                        e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 2px 8px rgba(16, 185, 129, 0.3)';
                      }}
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => handleDelete(tax.id)}
                      style={styles.deleteButton}
                      title="Delete Tax"
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-1px)';
                        e.target.style.boxShadow = '0 4px 12px rgba(220, 38, 38, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 2px 8px rgba(220, 38, 38, 0.3)';
                      }}
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div style={styles.noData}>
            <div style={{ fontSize: '4rem', marginBottom: '1rem', opacity: '0.5' }}>📦</div>
            <h3 style={{ color: '#4a5568', marginBottom: '0.5rem' }}>No GST Taxes Found</h3>
            <p style={{ marginBottom: '1.5rem' }}>Get started by adding your first tax!</p>
            <button 
              onClick={() => setCurrentPage('addForm')}
              style={{
                ...styles.addButton,
                fontSize: '1rem',
                padding: '1rem 2rem'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(37, 99, 235, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 15px rgba(37, 99, 235, 0.3)';
              }}
            >
              + Add Your First Tax
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TaxForm;